// Top-of-page time-range chip strip.
//
// Per the impeccable layout law this is *not* wrapped in a card — it's
// the page's primary control surface, so it sits on the page background.
// Two-to-seven mutually-exclusive options is exactly the case ToggleGroup
// was designed for; the "Custom" chip pops a side-by-side date-range
// calendar.

import { Calendar } from "@veil/ui/components/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@veil/ui/components/popover";
import { ToggleGroup, ToggleGroupItem } from "@veil/ui/components/toggle-group";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import {
	FLEET_RANGE_PRESETS,
	type FleetRangePreset,
	type FleetRangeValue,
} from "@/lib/use-fleet-range";

interface Props {
	value: FleetRangeValue;
	onPreset: (preset: FleetRangePreset) => void;
	onCustom: (from: Date, to: Date) => void;
}

const PRESET_LABEL: Record<FleetRangePreset, string> = {
	"1h": "1h",
	"6h": "6h",
	"24h": "24h",
	"7d": "7d",
	"30d": "30d",
};

export function RangeFilter({ value, onPreset, onCustom }: Props) {
	const [open, setOpen] = useState(false);
	const active = value.kind === "preset" ? value.preset : "custom";

	const customRange: DateRange | undefined =
		value.kind === "custom" ? { from: value.from, to: value.to } : undefined;

	return (
		<ToggleGroup
			type="single"
			variant="outline"
			size="sm"
			value={active}
			onValueChange={(next) => {
				if (!next) return;
				if (next === "custom") return;
				onPreset(next as FleetRangePreset);
			}}
			aria-label="Time range"
		>
			{FLEET_RANGE_PRESETS.map((p) => (
				<ToggleGroupItem key={p} value={p} aria-label={`Last ${p}`}>
					{PRESET_LABEL[p]}
				</ToggleGroupItem>
			))}
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<ToggleGroupItem
						value="custom"
						aria-label="Custom range"
						className="gap-1.5"
					>
						<CalendarIcon data-icon="inline-start" />
						{value.kind === "custom" ? value.label : "Custom"}
					</ToggleGroupItem>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="end">
					<Calendar
						mode="range"
						numberOfMonths={2}
						selected={customRange}
						onSelect={(next) => {
							if (next?.from && next.to) {
								// Snap `to` to end-of-day so the user gets the
								// whole selected day inclusive.
								const inclusiveTo = new Date(next.to);
								inclusiveTo.setHours(23, 59, 59, 999);
								onCustom(next.from, inclusiveTo);
								setOpen(false);
							}
						}}
						disabled={{ after: new Date() }}
					/>
				</PopoverContent>
			</Popover>
		</ToggleGroup>
	);
}
